export const paramSerialize = (obj, prefix) => {
  const serialize = (obj, prefix) => {
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        const k = prefix ? `${prefix}[${p}]` : p;
        const v = obj[p];
        str.push(
          v !== null && typeof v === "object"
            ? serialize(v, k)
            : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
        );
      }
    }
    return str.join("&");
  };

  return serialize(obj, prefix);
};

/**
 * Formats a string by replacing underscores with spaces and capitalizing words
 * @param {string} str - The string to format
 * @param {string} prefix - Optional prefix to add before each capitalized word
 * @returns {string} The formatted string
 */
export const formatString = (str, prefix = '') => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => prefix+char.toUpperCase());
};

/**
 * Truncates a string to a specified length and adds ellipsis if needed
 * @param {string} str - The string to truncate
 * @param {number} length - Maximum length of the string
 * @returns {string} The truncated string
 */
export const truncateString = (str, length = 50) => {
  if (!str) return '';
  return str.length > length ? `${str.substring(0, length)}...` : str;
};

/**
 * Converts a string to title case
 * @param {string} str - The string to convert
 * @returns {string} The title-cased string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Removes all whitespace from a string
 * @param {string} str - The string to clean
 * @returns {string} The cleaned string
 */
export const removeWhitespace = (str) => {
  if (!str) return '';
  return str.replace(/\s+/g, '');
};

/**
 * Converts a string to kebab case
 * @param {string} str - The string to convert
 * @returns {string} The kebab-cased string
 */
export const toKebabCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

/**
 * Converts a string to camel case
 * @param {string} str - The string to convert
 * @returns {string} The camel-cased string
 */
export const toCamelCase = (str) => {
  if (!str) return '';
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => 
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
};

/**
 * Checks if a string is a valid email address
 * @param {string} email - The email to validate
 * @returns {boolean} Whether the email is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Extracts numbers from a string
 * @param {string} str - The string to extract numbers from
 * @returns {string} The extracted numbers
 */
export const extractNumbers = (str) => {
  if (!str) return '';
  return str.replace(/\D/g, '');
};

/**
 * Generates a random string of specified length
 * @param {number} length - Length of the random string
 * @returns {string} The random string
 */
export const generateRandomString = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Checks if a string contains only letters
 * @param {string} str - The string to check
 * @returns {boolean} Whether the string contains only letters
 */
export const isAlphaOnly = (str) => {
  if (!str) return false;
  return /^[a-zA-Z]+$/.test(str);
};

/**
 * Checks if a string contains only numbers
 * @param {string} str - The string to check
 * @returns {boolean} Whether the string contains only numbers
 */
export const isNumericOnly = (str) => {
  if (!str) return false;
  return /^[0-9]+$/.test(str);
};

/**
 * Masks a string (e.g., for credit card numbers)
 * @param {string} str - The string to mask
 * @param {number} visibleChars - Number of characters to keep visible at the end
 * @param {string} maskChar - Character to use for masking
 * @returns {string} The masked string
 */
export const maskString = (str, visibleChars = 4, maskChar = '*') => {
  if (!str) return '';
  const length = str.length;
  if (length <= visibleChars) return str;
  return maskChar.repeat(length - visibleChars) + str.slice(-visibleChars);
}; 